import request from '@/utils/request'
import type { ResponseData } from '@/utils/request'
import type {
  UserListScheme,
  EmailConfigScheme,
  UserCreateScheme,
  ResetPasswordConfigScheme,
  UserRegisterConfigScheme,
  ActivateEmailConfigScheme,
  ICPConfigScheme,
  CalculateSatisfyValueScheme,
  SeaweedValueScheme,
  InletTemperatureScheme,
  AirTemperatureScheme,
  FlowInputConfigScheme,
  PressureDropInputConfigScheme,
} from './schemes'

// 分页查询管理员用户
export const getAdmins = (data: UserListScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/admins',
    method: 'POST',
    data,
  })
}

// 创建管理员用户
export const createAdmin = (data: UserCreateScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/admin',
    method: 'POST',
    data,
  })
}

// 锁定管理员用户
export const lockAdmin = (adminId: string): Promise<ResponseData<any>> => {
  return request({
    url: `/biz/admin/${adminId}/lock`,
    method: 'PUT',
  })
}

// 解锁管理员用户
export const unlockAdmin = (adminId: string): Promise<ResponseData<any>> => {
  return request({
    url: `/biz/admin/${adminId}/unlock`,
    method: 'PUT',
  })
}

// 删除管理员用户
export const deleteAdmin = (adminId: string): Promise<ResponseData<any>> => {
  return request({
    url: `/biz/admin/${adminId}`,
    method: 'DELETE',
  })
}

// 查询邮件配置
export const getEmailConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/config/email',
    method: 'GET',
  })
}

// 配置邮件服务
export const configEmail = (data: EmailConfigScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/config/email',
    method: 'POST',
    data,
  })
}

// 查询重置密码邮件配置
export const getPasswordResetEmailConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/reset_password_mail',
    method: 'GET',
  })
}

// 设置重置密码邮件配置
export const setPasswordResetEmailConfig = (
  data: ResetPasswordConfigScheme
): Promise<ResponseData<any>> => {
  return request({
    url: '/config/reset_password_mail',
    method: 'PUT',
    data,
  })
}

// 查询用户注册配置
export const getUserRegisterConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/user_register',
    method: 'GET',
  })
}

// 设置用户注册配置
export const setUserRegisterConfig = (
  data: UserRegisterConfigScheme
): Promise<ResponseData<any>> => {
  return request({
    url: '/config/user_register',
    method: 'PUT',
    data,
  })
}

// 查询激活邮件配置
export const getActivateEmailConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/active_mail',
    method: 'GET',
  })
}

// 设置激活邮件配置
export const setActivateEmailConfig = (
  data: ActivateEmailConfigScheme
): Promise<ResponseData<any>> => {
  return request({
    url: '/config/active_mail',
    method: 'PUT',
    data,
  })
}

// 查询系统备案信息
export const getICPConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/public/filing_information',
    method: 'GET',
  })
}

// 设置系统备案信息
export const setICPConfig = (data: ICPConfigScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/config/filing_information',
    method: 'PUT',
    data,
  })
}

// 查询用户账号默认有效期
export const getDefaultValidDays = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/default_valid_days',
    method: 'GET',
  })
}

// 设置用户账号默认有效期
export const setDefaultValidDays = (days: number): Promise<ResponseData<any>> => {
  return request({
    url: `/config/default_valid_days/${days}`,
    method: 'PUT',
  })
}

// 查询计算满足度配置
export const getCalculateSatisfyValue = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/filter/satisfy_value',
    method: 'GET',
  })
}

// 设置计算满足度配置
export const setCalculateSatisfyValue = (
  data: CalculateSatisfyValueScheme
): Promise<ResponseData<any>> => {
  return request({
    url: '/config/calculate_satisfy_value',
    method: 'PUT',
    data,
  })
}

// 查询计算精度
export const getCalculatePrecision = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/calculation_accuracy',
    method: 'GET',
  })
}

// 设置计算精度
export const setCalculatePrecision = (precision: number): Promise<ResponseData<any>> => {
  return request({
    url: `/config/calculation_accuracy/${precision}`,
    method: 'PUT',
  })
}

// 查询海拔上下限
export const getSeaweedValue = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/filter/seaweed_value',
    method: 'GET',
  })
}

// 设置海拔上下限
export const setSeaweedValue = (data: SeaweedValueScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/config/seaweed_value',
    method: 'PUT',
    data,
  })
}

// 查询入口温度上下限
export const getInletTemperatureValue = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/filter/inlet_temperature_value',
    method: 'GET',
  })
}

// 设置入口温度上下限
export const setInletTemperatureValue = (
  data: InletTemperatureScheme
): Promise<ResponseData<any>> => {
  return request({
    url: '/config/inlet_temperature_value',
    method: 'PUT',
    data,
  })
}

// 查询空气温度上下限
export const getAirTemperatureValue = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/filter/air_temperature_value',
    method: 'GET',
  })
}

// 设置空气温度上下限
export const setAirTemperatureValue = (data: AirTemperatureScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/config/air_temperature_value',
    method: 'PUT',
    data,
  })
}

// 查询当量冷却能力精度
export const getCoolerAbilityAccuracy = (): Promise<ResponseData<any>> => {
  return request({
    url: '/config/cooler_ability_accuracy',
    method: 'GET',
  })
}

// 设置当量冷却能力精度
export const setCoolerAbilityAccuracy = (accuracy: number): Promise<ResponseData<any>> => {
  return request({
    url: `/config/cooler_ability_accuracy/${accuracy}`,
    method: 'PUT',
  })
}

// 查询流量计算配置的上下限
export const getFlowInputConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/filter/oil_flow_value',
    method: 'GET',
  })
}

// 设置流量计算配置的上下限
export const setFlowInputConfig = (data: FlowInputConfigScheme): Promise<ResponseData<any>> => {
  return request({
    url: '/config/oil_flow_value',
    method: 'PUT',
    data,
  })
}

// 查询压降计算配置的上下限
export const getPressureDropInputConfig = (): Promise<ResponseData<any>> => {
  return request({
    url: '/biz/filter/pressure_drop_value',
    method: 'GET',
  })
}

// 设置压降计算配置的上下限
export const setPressureDropInputConfig = (
  data: PressureDropInputConfigScheme
): Promise<ResponseData<any>> => {
  return request({
    url: '/config/pressure_drop_value',
    method: 'PUT',
    data,
  })
}
